import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLockGuard, LOCK_WATCHDOG_TEXT } from "@/shared/lib/lockGuard";
import { advisorChatApi } from "../api/advisorChatApi";
import {
  appendUser,
  applyTurn,
  initialChat,
  isConfirmGate,
  noteSupersededConfirm,
} from "./chatState";
import type { AdvisorTurnData, ChatState } from "./chatTypes";
import { describeAdvisorError, type AdvisorErrorInfo } from "./advisorError";
import { toUploadRefs, useUploadTray } from "./advisorUploads";
import { getPersona } from "@/shared/brand/persona";

// 按 sessionId 持久化会话态,使 launcher 抽屉关闭再打开(面板卸载再挂载)不丢消息/待确认卡片。
const sessionCache = new Map<string, ChatState>();

/** 测试隔离用:清空跨 mount 的会话缓存。 */
export function __clearAdvisorChatCache() {
  sessionCache.clear();
}

/** proactive 开场:每会话一次(staleTime/gcTime Infinity,按 sessionId keying),不每次进 tab 烧 LLM。 */
export function useProactiveOpen(sessionId: string, enabled = true) {
  return useQuery<AdvisorTurnData>({
    queryKey: ["advisor", "proactive", sessionId],
    queryFn: () => advisorChatApi.sessionOpen(sessionId),
    // ADV-UI-2:满屏页开场改走 briefing(三块开屏),proactive 开场白只在浮层模式拉 ——
    // 两个都拉会双问候还双烧 LLM。
    enabled,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
  });
}

export function useAdvisorChat(sessionId: string) {
  // 懒读缓存:首次(无缓存)→ initialChat(),与现状一致;卸载再挂载 → 恢复上次会话态。
  const [state, setState] = useState<ChatState>(() => sessionCache.get(sessionId) ?? initialChat());
  // bug 3:错误只剩「请求失败」四个字 —— 现在留住 errorCode/traceId,并在下一次成功时清掉
  // (原来错误条一挂就不走,任务其实已经跑成了,用户还以为没做成)。
  const [error, setErrorInfo] = useState<AdvisorErrorInfo | null>(null);
  const setError = (e: unknown, fallback: string) =>
    setErrorInfo(describeAdvisorError(e, fallback));

  // state 每次变化写回缓存,使下一次同 sessionId 挂载能恢复。
  useEffect(() => {
    sessionCache.set(sessionId, state);
  }, [sessionId, state]);

  // ADV-LOCK-1:锁态统一出口 —— 四个入口此前各写一遍 setLoading(true)…finally setLoading(false),
  // **同构且都只在 fetch settle 时解锁**。收成共享守卫后每个入口自带看门狗(见 lockGuard 头注)。
  const { loading, runLocked } = useLockGuard(sessionId, () =>
    setErrorInfo({ text: LOCK_WATCHDOG_TEXT, errorCode: "LOCK_WATCHDOG", traceId: "" })
  );

  // S-photo:对话附件盘(与 H5 共用同一份逻辑,见 advisorUploads)。
  const { pendingUploads, attachUpload, removeUpload, clearUploads } = useUploadTray(sessionId);

  const send = useCallback(
    async (message: string) => {
      setErrorInfo(null);
      // 乐观追加用户气泡(本地线程 next,避免 stale state 丢历史)
      let next = appendUser(state, message);
      setState(next);
      await runLocked("/advisor/chat", async () => {
        try {
          const turn = await advisorChatApi.chat(sessionId, message, toUploadRefs(pendingUploads));
          // 附件盘只在**非门控** turn 清空:门控(awaiting_confirm)时保留,好让 resume 重发
          // 上传图集(ai turn_resume 据 body.uploadedImages 重建 ctx.uploaded_images)。
          if (!turn.confirm) clearUploads();
          // B2 #8⑦:确认卡挂着时又发了话 → 旧卡被顶掉要说出来,不静默换卡
          next = applyTurn(noteSupersededConfirm(next, turn), turn);
          setState(next);
        } catch (e) {
          setError(e, `${getPersona().title}暂时无法回复`);
        }
      });
    },
    [sessionId, state, runLocked, pendingUploads, clearUploads]
  );

  // 开新话题:清 ai 工作上下文(下一次 proactive open 空开局 mint 新 topic)+ 清本地会话态(messages/画布/缓存)。
  // 记忆不受影响(profile/procedural/episodic 仍每 turn 注入)→ 干净但不失忆。
  // proactive 重开由 AdvisorChatPanel 侧 resetQueries(hook 不持 QueryClient,保持无 react-query 依赖)。
  // loading 期间禁发送/禁重复开新话题(codex console P2:防 reset 在途时发消息 race);失败保留当前会话不清。
  const newTopic = useCallback(async () => {
    setErrorInfo(null);
    await runLocked("/advisor/topics/new", async () => {
      try {
        await advisorChatApi.newTopic();
        sessionCache.delete(sessionId);
        setState(initialChat());
        clearUploads(); // 干净开局:上一话题没发出去的附图不该跟到新话题
      } catch (e) {
        setError(e, "开新话题失败");
        throw e; // 抛出 → panel 的 .then 不触发 proactive 重置(失败不重开)
      }
    });
  }, [sessionId, runLocked, clearUploads]);

  /**
   * #42 进入回灌:本地会话态是 module 级内存 Map,**刷新即清零**,而转录一直在库里
   * (实测 /advisor/topics/{id}/thread 原样返回)。这里把拉回来的历史灌进空线程。
   * ★只在本地线程**为空**时生效,绝不覆盖眼前这一轮 —— 历史盖掉当前比不回灌更糟。
   */
  /**
   * ★1004 A1:第二个参数 = 服务端探针交回的**还挂着的确认卡**。刷新前卡没回答,刷新后转录里
   *   只剩卡面那段文字、按钮没了 —— 用户只能打字,而那句话会被当成续接旧卡(走查 2/2)。
   *   与消息**同一次 setState、同一道「空线程才灌」守卫**:眼前已经开了新一轮(用户刷新后
   *   抢先发了话),服务端那张卡已被顶掉,再摆回来就是一张点了只会提示过期的卡。
   */
  const hydrateMessages = useCallback(
    (msgs: { role: "user" | "advisor"; text: string }[], pendingConfirm?: unknown) => {
      if (msgs.length === 0) return;
      const card = isConfirmGate(pendingConfirm) ? pendingConfirm : null;
      setState((prev) =>
        prev.messages.length > 0
          ? prev
          : {
              ...prev,
              messages: msgs.map((m) => ({ ...m })),
              pendingConfirm: prev.pendingConfirm ?? card,
            }
      );
    },
    []
  );

  /**
   * 显式切换话题(用户在历史里点「从这条继续」)→ **整体替换**当前线程。
   * ★与 `hydrateMessages` 是两件事,别合并:
   *   - `hydrateMessages` 是**被动**恢复(刷新后接回来),空线程才灌,绝不覆盖;
   *   - 这个是**用户明确指令**,就是要把眼前这段换掉。
   *   合成一个函数迟早有人用错那一半,而用错的方向是"历史盖掉了正在聊的" —— 静默且不可逆。
   * ★顺带清掉 pendingConfirm:那张确认卡属于**上一条**话题,跟过来就会点出错的东西。
   */
  const replaceMessages = useCallback((msgs: { role: "user" | "advisor"; text: string }[]) => {
    setState({ messages: msgs.map((m) => ({ ...m })), pendingConfirm: null });
  }, []);

  const resolveConfirm = useCallback(
    async (confirmed: boolean) => {
      await runLocked("/advisor/resume", async () => {
        try {
          // I8:回传当前确认卡 nonce;pendingConfirm 在依赖里,不吃闭包旧值
          const pid = state.pendingConfirm?.id ?? "";
          const turn = await advisorChatApi.resume(
            sessionId,
            confirmed,
            pid,
            toUploadRefs(pendingUploads)
          );
          clearUploads(); // 门控 turn 已了结(confirm/cancel),附件盘清空
          setState((prev) => applyTurn({ ...prev, pendingConfirm: null }, turn));
        } catch (e) {
          setState((prev) => ({ ...prev, pendingConfirm: null }));
          setError(e, "提交失败");
        }
      });
    },
    [sessionId, state.pendingConfirm, runLocked, pendingUploads, clearUploads]
  );

  // C2:确认门风格条点卡 → 确定性 patch pending.style 后重报价(不经 LLM turn),用刷新后的确认门替换。
  // pendingConfirm 在依赖里(不吃闭包旧值);失败保留原卡不清(用户可重点或直接确认默认)。
  const pickStyle = useCallback(
    async (styleKey: string) => {
      const pid = state.pendingConfirm?.id;
      if (!pid) return;
      await runLocked("/advisor/turn/pick-style", async () => {
        try {
          const turn = await advisorChatApi.pickStyle(sessionId, styleKey, pid);
          // pick-style 是确认门内的原地操作:busy/disabled/timeout 这类「没落地」的回包(confirm:null)
          // 绝不能经 applyTurn 把用户眼前的确认门清空(否则整张海报再也没法确认)。只有 awaiting_confirm
          // (门已刷新)或 committed(过期/已解决)才据实替换;其余保留原门,把原因提示出来(红队 2026-07-09)。
          if (turn.status === "awaiting_confirm" || turn.status === "committed") {
            setState((prev) => applyTurn(prev, turn));
          } else if (turn.text) {
            setErrorInfo({ text: turn.text, errorCode: "", traceId: "" });
          }
        } catch (e) {
          setError(e, "选风格失败");
        }
      });
    },
    [sessionId, state.pendingConfirm, runLocked]
  );

  return {
    messages: state.messages,
    pendingConfirm: state.pendingConfirm,
    loading,
    error,
    send,
    newTopic,
    pickStyle,
    confirm: () => resolveConfirm(true),
    cancel: () => resolveConfirm(false),
    pendingUploads,
    attachUpload,
    removeUpload,
    hydrateMessages,
    replaceMessages,
  };
}
